// =============================================
// مكون البطل (Hero) - DATX
// تصميم عالمي احترافي بثلاثة ألوان فقط
// صور بيانات وداشبوردات بهدوء
// =============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';

// =============================================
// صور بيانات وداشبوردات - داكنة ومناسبة
// =============================================
const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1280&q=75&auto=format&fit=crop",
    fallback: ""
  },
  {
    url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1280&q=75&auto=format&fit=crop",
    fallback: ""
  },
  {
    url: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=1280&q=75&auto=format&fit=crop",
    fallback: ""
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const timerRef = useRef(null);
  const imageCache = useRef({});

  const slides = [
    {
      title: "تحليل البيانات",
      subtitle: "وجهتك الرائدة في حلول وتعليم تحليل البيانات وعلومها",
      description: "نقدم لك تجربة تعليمية متكاملة تجمع بين الجودة والتطبيق العملي، لتمكينك من اكتساب المهارات المطلوبة في سوق العمل."
    },
    {
      title: "علوم البيانات",
      subtitle: "أتقن علوم البيانات مع أفضل الخبراء",
      description: "دورات تدريبية متخصصة ومنهجيات عملية حديثة، تؤهلك لتصبح خبيراً في تحليل البيانات واتخاذ القرارات الاستراتيجية."
    },
    {
      title: "الذكاء الاصطناعي",
      subtitle: "تعلم أساسيات الذكاء الاصطناعي وتطبيقاته",
      description: "برامج تدريبية متكاملة تغطي أحدث التقنيات في مجال الذكاء الاصطناعي، مع تطبيقات عملية في تحليل البيانات."
    }
  ];

  const heroContainerRef = useRef(null);
  const [isVisibleInViewport, setIsVisibleInViewport] = useState(true);

  // =============================================
  // تحميل الصور تدريجياً: الصورة الأولى فوراً ثم البقية
  // =============================================
  useEffect(() => {
    // 1. تحميل الصورة الأولى فوراً
    const firstImg = new Image();
    firstImg.src = HERO_IMAGES[0].url;
    firstImg.onload = () => {
      setImagesLoaded(prev => ({ ...prev, 0: true }));
      imageCache.current[0] = firstImg;

      // 2. تأجيل تحميل باقي الصور بعد اكتمال الصورة الأولى
      HERO_IMAGES.slice(1).forEach((img, idx) => {
        const nextIndex = idx + 1;
        const nextImg = new Image();
        nextImg.src = img.url;
        nextImg.onload = () => {
          setImagesLoaded(prev => ({ ...prev, [nextIndex]: true }));
          imageCache.current[nextIndex] = nextImg;
        };
      });
    };
    firstImg.onerror = () => {
      setImagesLoaded(prev => ({ ...prev, 0: true }));
    };
  }, []);

  // =============================================
  // مراقبة ظهور الـ Hero في الشاشة لتوفير المعالج
  // =============================================
  useEffect(() => {
    if (!heroContainerRef.current || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisibleInViewport(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(heroContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // =============================================
  // التحكم في التبديل التلقائي (يتوقف خارج الشاشة)
  // =============================================
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (isPlaying && isVisibleInViewport) {
      timerRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4500);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [slides.length, isPlaying, isVisibleInViewport]);

  // الانتقال إلى شريحة محددة
  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  return (
    <div ref={heroContainerRef} className="relative h-screen overflow-hidden bg-[#020B14]">
      {/* =============================================
          خلفية السلايدر - تظهر فوراً
          ============================================= */}
      <div className="absolute inset-0">
        {HERO_IMAGES.map((img, index) => {
          // الصورة الأولى تظهر فوراً، الباقي يظهر بعد التحميل
          const shouldShow = currentSlide === index && (imagesLoaded[index] || index === 0);
          
          return (
            <motion.div
              key={index}
              className="absolute inset-0"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ 
                opacity: shouldShow ? 1 : 0,
                scale: shouldShow ? 1 : 1.05
              }}
              transition={{ 
                duration: 1.2,
                ease: [0.25, 0.1, 0.25, 1],
                delay: currentSlide === index && index === 0 ? 0 : 0
              }}
              style={{
                willChange: 'opacity, transform',
                backfaceVisibility: 'hidden'
              }}
            >
              <img
                src={img.url}
                alt="DATX Data & AI Platform"
                width="1280"
                height="720"
                fetchPriority={index === 0 ? "high" : "low"}
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                className="absolute left-1/2 top-1/2 min-w-full min-h-full object-cover -translate-x-1/2 -translate-y-1/2"
                style={{
                  willChange: 'transform',
                  transform: 'translate(-50%, -50%)',
                  filter: 'brightness(0.35) saturate(0.8) contrast(1.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              
              {/* طبقات التدرج الداكنة */}
              <div className="absolute inset-0" style={{
                background: `
                  linear-gradient(135deg, 
                    rgba(2,11,20,0.92) 0%, 
                    rgba(2,11,20,0.80) 30%, 
                    rgba(2,11,20,0.60) 60%,
                    rgba(2,11,20,0.40) 100%
                  )
                `
              }} />
              
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(to top, rgba(2,11,20,0.95) 0%, transparent 50%)'
              }} />
              
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(to bottom, rgba(2,11,20,0.3) 0%, transparent 30%)'
              }} />
            </motion.div>
          );
        })}
      </div>

      {/* =============================================
          تأثيرات ضوئية خفيفة
          ============================================= */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full" style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)'
        }} />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full" style={{
          background: 'radial-gradient(circle, rgba(96,165,250,0.04) 0%, transparent 70%)'
        }} />
      </div>

      {/* =============================================
          المحتوى الرئيسي
          ============================================= */}
      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <div className="flex flex-col justify-center h-full">
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ 
                duration: 0.8, 
                ease: [0.25, 0.1, 0.25, 1]
              }}
              className="max-w-3xl"
            >
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-5 leading-tight"
                style={{ textShadow: '0 4px 30px rgba(0,0,0,0.3)' }}
              >
                <span className="bg-gradient-to-r from-blue-300 via-indigo-200 to-cyan-300 bg-clip-text text-transparent">
                  {slides[currentSlide].title}
                </span>
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-xl md:text-2xl lg:text-3xl text-white font-medium mb-4 leading-relaxed max-w-2xl"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
              >
                {slides[currentSlide].subtitle}
              </motion.p>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-base md:text-lg text-blue-100/90 font-medium mb-9 leading-relaxed max-w-xl"
                style={{ textShadow: '0 2px 15px rgba(0,0,0,0.4)' }}
              >
                {slides[currentSlide].description}
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/courses"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#00131D] rounded-xl font-semibold text-base md:text-lg transition-all duration-300 hover:shadow-2xl hover:shadow-white/20 relative overflow-hidden group"
                  >
                    <span className="relative z-10">ابدأ التعلم</span>
                    <ArrowLeft className="w-5 h-5 relative z-10 group-hover:-translate-x-1 transition-transform" />
                  </Link>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white rounded-xl font-semibold text-base md:text-lg transition-all duration-300 hover:bg-white/10 hover:border-white/50"
                    style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
                  >
                    <Play className="w-4 h-4 text-blue-300 fill-blue-300/30" />
                    اعرف المزيد
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* =============================================
          مؤشرات الشرائح + زر إيقاف وتشغيل بسيط وصغير
          ============================================= */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10 bg-black/25 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10 shadow-lg">
        {slides.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            className={`relative h-1.5 rounded-full transition-all duration-500 ${
              currentSlide === index ? 'bg-white w-10' : 'bg-white/30 w-5 hover:bg-white/60'
            }`}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.95 }}
            aria-label={`اذهب إلى الشريحة ${index + 1}`}
          >
            {currentSlide === index && isPlaying && (
              <motion.div
                className="absolute inset-0 bg-white rounded-full"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ 
                  duration: 4, 
                  ease: "linear",
                  repeat: Infinity
                }}
              />
            )}
          </motion.button>
        ))}

        {/* فاصل صغير جداً */}
        <div className="w-[1px] h-3 bg-white/20 mx-0.5" />

        {/* زر إيقاف / تشغيل بسيط وصغير */}
        <motion.button
          onClick={togglePlayPause}
          className="w-5 h-5 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
          whileHover={{ scale: 1.25 }}
          whileTap={{ scale: 0.9 }}
          title={isPlaying ? 'إيقاف التحريك التلقائي' : 'تشغيل التحريك التلقائي'}
          aria-label={isPlaying ? 'إيقاف التحريك التلقائي' : 'تشغيل التحريك التلقائي'}
        >
          {isPlaying ? (
            <Pause className="w-3 h-3" />
          ) : (
            <Play className="w-3 h-3 fill-current" />
          )}
        </motion.button>
      </div>

      {/* =============================================
          منحنى سفلي
          ============================================= */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 50L80 42C160 34 320 18 480 14C640 10 800 18 960 23C1120 28 1280 30 1360 31L1440 32V50H1360C1280 50 1120 50 960 50H480C320 50 160 50 80 50H0Z" fill="white"/>
        </svg>
      </div>
    </div>
  );
};

export default Hero;